import { QMProject } from '@scale/shared/qm/qmRequestTypes';
import { IUserJson } from '@scale/shared/types/user';

export enum SectionTitle {
  Overview = 'Overview',
  SpotterPipeline = 'Zero Review + Spotter Pipeline',
  GeneralConfig = 'General Config',
  Throughput = 'Throughput',
  DisablingLinting = 'Disabling & Linting',
  Training = 'Training',
  Incentives = 'Incentives',
  Badges = 'Badges',
  Problems = 'Problems',
  Benchmarks = 'Benchmarks',
  Subtasks = 'Subtasks',
  ArchetypeSelector = 'Archetype Selector',
  TaskParamsEditor = 'Task Params Editor',
  TaskTemplatesEditor = 'Task Templates Editor',
  SpecializedReviewRouting = 'Specialized Routing',
  OntologyManager = 'Ontology Manager',
  DocumentTaskCreator = 'Document Task Creator',
  PipelineEditor = 'Pipeline Editor',
  ZeroReviewSetupChecklist = 'Zero Review + Spotter Pipeline Setup List',
  ZeroReviewDailyChecklist = 'Zero Review + Spotter Pipeline Daily CheckLlst',
  Checklists = 'CheckLists',
  InlineTools = 'Inline Tools',
  Autopilot = 'Autopilot',
  JsonViewer = 'JSON Viewer',
  IncidentFlags = 'Incident Flags',
  Config = 'Full Configs',
  QualityMetrics = 'Quality Metrics',
  ReviewerFeedback = 'Reviewer Feedback',
  SpeedAudit = 'Speed Audit',
  BenchmarkSpeedAudit = 'Benchmark Speed Audit',
  AuditDashboard = 'Audit Dashboard',
  WorkerPerformance = 'Worker Performance',
  QueryPlayground = 'Query Playground',
  GraderConfigsEditor = 'Grader Configs Editor',
  ProjectOwners = 'Project Owners',
  DisableProject = 'Enable & Disable Project',
  Priority = 'Queue Priority',
  Tasks = 'Tasks',
  ActionDirectory = 'Action Directory',
  QuickLinters = 'Quick Linters',
  ActionBatches = 'Action Batches',
  TaskerOperations = 'Tasker Operations',
  ProjectTaxonomyVersions = 'Project Taxonomy Versions',
  ProjectDocumentsAndAnnouncements = 'Project Documents & Announcements',
  StaffingExceptionForm = 'Staffing Exception Form',
  InstructionsTaxonomy = 'Instructions/Taxonomy Editors (NextGen)',
  BillingVersionEditor = 'Billing Versions',
  PayRateCardPreview = 'Pay Rate Card Preview',
  Qualifications = 'Qualifications',
  PaySandbox = 'Pay Sandbox',
  AVTaxonomyService = 'Taxonomies + Instructions (Teddy)',
}

export enum ChecklistItemType {
  Triage = 'Triage',
  Action = 'Action',
}

export enum ActionItem {
  CorpExplain = 'corp_explain',
  AllocationExplain = 'allocation_explain',
  MisconfiguredSupplyLevers = 'misconfigured_supply_levers',
  Throttles = 'throttles',
  TableauAccess = 'tableau_access',
  CapacityAllocation = 'capacity_allocation',
  ThroughputOpsProcesses = 'throughput_ops_processes',
  PermissionAndAssignAttempters = 'permission_and_assign_attempters',
  PermissionAndAssignReviewers = 'permission_and_assign_reviewers',
  PermissionAndAssignPipelineV3QIRAuditors = 'permission_and_assign_pipeline_v3_qir_auditors',
  MassUnassignProject = 'mass_unassign_project',
  WorkerSources = 'worker_sources',
  Placeholder = 'placeholder_action_item',
  RetroactiveEnableDisable = 'retroactive_enable_disable',
  BenchmarkConfiguration = 'benchmark_configuration',
  BenchmarkEnableOrDisable = 'benchmark_enable_or_disable',
  BenchmarkServingConfigs = 'benchmark_serving_configs',
  BenchmarkNiceConfigs = 'benchmark_nice_configs',
  SendTasksToCustomer = 'send_tasks_to_customer',
  QuarantineAndUnquarantineSubtasks = 'quarantine_and_unquarantine_subtasks',
  MoveTasksInPipeline = 'move_tasks_in_pipeline',
  MoveTasksInPipelineV3 = 'move_tasks_in_pipeline_v3',
  TuneMinNumBenchmarksConfig = 'tune_min_num_benchmarks_config',
  TuneDisablingThresholdConfig = 'tune_disabling_threshold_config',
  TuneMinNumInstructionItemBenchmarksConfig = 'tune_min_num_instruction_item_benchmarks_config',
  TuneInstructionItemDisablingThresholdConfig = 'tune_instruction_item_disabling_threshold_config',
  AllProjectCourses = 'all_project_courses',
  CourseStarts = 'course_starts',
  CoursePath = 'course_path',
  CourseBlockingPoints = 'course_blocking_points',
  CourseScenarios = 'course_scenarios',
  GenerateConfusionMatrix = 'generate_confusion_matrix',
  GenerateFeedbackCenter = 'generate_feedback_center',
  PersonalizedCourses = 'personalized_courses',
  InstructionItemManager = 'instruction_item_manager',
  InstructionUpdateChangelog = 'instruction_update_changelog',
  CheckForLongTailTasks = 'check_for_long_tail_tasks',
  DemoteSuspiciousReviewers = 'demote_suspicious_reviewers',
  AssignTrustedWorkersLTT = 'assign_trusted_reviewers',
  CheckAssignRejectionsConfig = 'check_assign_rejections_config',
  SpeedAuditLTT = 'speed_audit_ltt',
  TuneLinters = 'tune_linters',
  LinterConfig = 'linter_config',
  RemedialCourses = 'remedial_courses',
  LinterDebugging = 'linter_debugging',
  AuditLintResults = 'audit_lint_results',
  PermissionAndAssignSuperAttempters = 'permission_and_assign_super_attempters',
  PermissionAndAssignSpotters = 'permission_and_assign_spotters',
  SubtaskReservation = 'subtask_reservation',
  DemoteSuperAttempters = 'demote_super_attempters',
  DemoteReviewers = 'demote_reviewers',
  MassDisableTaskers = 'mass_disable_taskers',
  DemoteSpotters = 'demote_spotters',
  SpotterPerformanceDecisionTree = 'spotter_performance_decision_tree',
  SpotterQualityManualMonitoring = 'spotter_quality_manual_monitoring',
  ReleaseAuditStages = 'release_audit_stages',
  SpotterAuditTool = 'spotter_audit_tool',
  PromoteAttemptersToSpotters = 'promote_attempters_to_spotters',
  DemoteSpottersToAttempters = 'demote_spotters_to_attempters',
  TriagePausedPermissionGroup = 'triage_paused_permission_group',
  AnalyzeInlineComments = 'analyze_inline_comments',
}

export interface ActionItemComponentProps<A extends ActionItem> {
  actionItem: A;
  user: IUserJson;
  project: QMProject;
}

export enum TriageItem {
  InsufficientCapacityAllocations = 'insufficient_capacity_allocations',
  LowBenchmarkConversion = 'low_benchmark_conversion',
  LowInstructionItemBenchmarkConversion = 'low_instruction_item_benchmark_conversion',
  LowCoursesConversion = 'low_courses_conversion',
  LongTailTasks = 'long_tail_tasks',
  Placeholder = 'placeholder_triage_item',
}

export interface IChecklistActionItem {
  text: string;
  buttonText: string;
  checklistType: ChecklistItemType.Action;
  actionItem: ActionItem;
  shouldIndent?: boolean;
  shouldDoubleIndent?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

export interface IChecklistTriageItem {
  text: string;
  buttonText: string;
  checklistType: ChecklistItemType.Triage;
  triageItem: TriageItem;
  shouldIndent?: boolean;
  shouldDoubleIndent?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

export type IChecklistItem = IChecklistActionItem | IChecklistTriageItem;

export interface IActionSectionChecklist {
  title: string;
  children: Array<IChecklistActionItem>;
  subtitle?: string;
}

export interface ISectionChecklist {
  title: string;
  children: Array<IChecklistItem>;
  hideCheckboxes?: boolean;
  subtitle?: string;
}

export interface IGenericSubSection {
  title: string;
  children: Array<ISectionChecklist>;
}

export interface ITriageSubSection {
  title: string;
  children: Array<IActionSectionChecklist>;
}

export type ISubSection = IGenericSubSection | ITriageSubSection;

export interface ISection {
  title: SectionTitle;
  children: Array<ISubSection>;
}
